














































































import Debug from "debug";
import { Button, Icon, Spin } from "ant-design-vue";
import Vue, { VueConstructor } from "vue";
import NProgress from "nprogress";
import { mapState, mapActions } from "vuex";
import { AxiosError } from "axios";
import store from "@/store";
import KYC from "@/store/kyc";
import { apiConfig, displayErrors } from "@/utils";
import { api as apiTrade, authorize } from "@/api";
import { api as apiKyc, getCurrencies } from "@/api/kyc";
import IconBack from "@/components/icon-back.svg";
import WelcomeEnter from "@/components/kyc/WelcomeEnter.vue";
import ChooseType from "@/components/kyc/ChooseType.vue";
import ListIndividual from "@/components/kyc/Individual/List.vue";
import ListInstitution from "@/components/kyc/Institution/List.vue";
import KycIndividual from "@/components/kyc/Individual/Index.vue";
import KycInstitution from "@/components/kyc/Institution/Index.vue";
import ApplyDone from "@/components/kyc/ApplyDone.vue";

const log = Debug("legend");

if (store.hasModule("KYC")) {
  store.unregisterModule("KYC");
}
store.registerModule("KYC", KYC);

interface VuexBindings {
  clientInfo: { [index: string]: string };
  token: string;
  applyType: number;
  currentStep4Individual: number;
  currentStep4Institution: number;
}

export default (Vue as VueConstructor<Vue & VuexBindings>).extend({
  name: "Kyc",

  data() {
    return {
      loading: false
    };
  },

  components: {
    [Button.name]: Button,
    [Button.Group.name]: Button.Group,
    [Icon.name]: Icon,
    [Spin.name]: Spin,
    IconBack,
    WelcomeEnter,
    ChooseType,
    ListIndividual,
    ListInstitution,
    KycIndividual,
    KycInstitution,
    ApplyDone
  },

  props: {
    role: {
      type: String,
      default: ""
    },
    mode: {
      type: String,
      default: ""
    },
    remote: {
      type: String,
      default: ""
    },
    lang: {
      type: String,
      default: "en"
    },
    code: {
      type: String,
      default: ""
    },
    state: {
      type: Number,
      default: 0
    },
    retry: {
      type: Number,
      default: 0
    },
    step: {
      type: Number,
      default: 0
    }
  },

  computed: {
    ...mapState(["clientInfo", "token"]),
    ...mapState("KYC", [
      "currentStep4Individual",
      "currentStep4Institution",
      "applyType"
    ]),
    isBroker(): boolean {
      return this.role === "broker";
    },
    isLocal(): boolean {
      return apiConfig.reHostname.test(window.location.hostname);
    },
    isDev(): boolean {
      return apiConfig.reDevHostname.test(window.location.hostname);
    },
    isSandbox(): boolean {
      return this.mode === "sandbox";
    },
    isDemo(): boolean {
      return this.mode === "demo";
    },
    spinning(): boolean {
      return !this.token && this.loading && this.state > 1 && this.state < 20;
    },
    kycInfo(): { state: number; type: number } {
      return { state: this.state, type: this.applyType };
    },
    getTitle(): string {
      if (this.isBroker) return "Become a Partner";
      const { state } = this;
      let title = "Trade with Us";
      switch (state) {
        case 0:
        case 1: {
          title = "Trade with Us";
          break;
        }
        case 10:
        case 20: {
          title = "Onboarding - Individual";
          break;
        }
        case 11:
        case 21: {
          title = "Onboarding - Institution";
          break;
        }
      }
      return title;
    }
  },
  watch: {
    async role() {
      this.setToken("");
      this.setupEnv();
      await this.getSign();
    },
    kycInfo: {
      async handler({ state, type }) {
        log("watch state: %o", this.token);
        if (state >= 1 && type >= 0 && !this.token) {
          this.setupEnv();
          await this.getSign();
        }
      },
      immediate: true,
      deep: true
    },
    lang: {
      handler(val) {
        if (this.$i18n.locale !== val) {
          this.$i18n.locale = val;
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions(["updateClientInfo", "setToken", "setCurrencies"]),
    ...mapActions("KYC", [
      "changeCurrentStep4Individual",
      "changeCurrentStep4Institution",
      "changeApplyType"
    ]),

    setupEnv() {
      if (process.env.NODE_ENV === "development") {
        // respect env config during develpoment
      } else if (this.isLocal || this.isDev) {
        // sandbox not work during local build
        if (this.remote) {
          // setup remote host from location.search
          apiConfig.remote = this.remote;
        }
        apiConfig.env = "development";
        apiTrade.defaults.baseURL = apiConfig.baseUrlTrade;
        apiKyc.defaults.baseURL = apiConfig.baseUrlKyc;
      } else if (this.isSandbox || this.isDemo) {
        apiConfig.env = "sandbox";
        apiTrade.defaults.baseURL = apiConfig.baseUrlTrade;
        apiKyc.defaults.baseURL = apiConfig.baseUrlKyc;
      }
    },
    async getSign() {
      try {
        this.loading = true;
        const $meta = document.querySelector("meta[name=sign]");
        const data = JSON.parse($meta?.getAttribute("content") as string);
        log("current signature: %o", data);
        if (!data.signature) {
          throw new Error("invalid sinature");
        }
        if (!data.timestamp) {
          throw new Error("invalid timestamp");
        }
        this.updateClientInfo({
          timestamp: data.timestamp,
          signature: data.signature,
          userType: this.role,
          appId: data["APP-ID"],
          appKey: data["APP-KEY"],
          appUid: data["APP-UID"],
          appUrl: data["APP-URL"],
          code: this.code
        });
        await this.getToken();
        this.loading = false;
      } catch (err) {
        const { status, data } = (err as AxiosError).response || {};
        const retry =
          (err as Error).message.includes("JSON") ||
          (err as Error).message.startsWith("invalid") ||
          (status === 422 && data?.message?.startsWith?.("Timestamp"));
        if (retry && this.retry < 3 && !this.token) {
          // Timestamp is not valid.
          // access_token with error, refresh page
          this.$router
            .replace({
              query: {
                ...this.$route.query,
                retry: (this.retry + 1).toString()
              }
            })
            .then(() => {
              log("undefined access_token, refresh.");
              window.location.reload();
            })
            .catch(err => err);
        } else {
          displayErrors(err);
          this.loading = false;
        }
      }
    },

    async getToken() {
      const { query } = this.$route;
      NProgress.start();
      const { access_token } = await authorize(this.clientInfo);
      this.setToken(access_token);
      if (access_token) {
        const currencies = await getCurrencies();
        this.setCurrencies(currencies);
        this.$router
          .replace({
            query: { ...query, retry: undefined, state: query.state }
          })
          .catch(err => err);
      }
      NProgress.done();
    },
    navBack() {
      const { state, isBroker } = this;
      if (state === 20 && this.currentStep4Individual > 0) {
        this.changeCurrentStep4Individual(this.currentStep4Individual - 1);
      } else if (state === 21 && this.currentStep4Institution > 0) {
        this.changeCurrentStep4Institution(this.currentStep4Institution - 1);
      } else if (state > 0) {
        this.$router.back();
      } else if (isBroker) {
        this.$router.push("/partner");
      } else {
        this.$router.push("/");
      }
    }
  },

  created() {
    this.setupEnv();

    if (this.state > 1) {
      // set apply type on refill
      this.changeApplyType(this.state % 10);

      // set step for debug
      if (this.step > 0) {
        if (this.applyType === 0) {
          this.changeCurrentStep4Individual(this.step);
        } else if (this.applyType === 1) {
          this.changeCurrentStep4Institution(this.step);
        }
      }
    }
  }
});
